import { z } from 'zod';
import {
  BillingCycle,
  BillingCycleSchema,
  BusinessSubscriptionStatus,
  BusinessSubscriptionStatusSchema,
} from '../enums/subscription.enums';

// Re-export for backwards compatibility
export { BillingCycle, BillingCycleSchema, BusinessSubscriptionStatus, BusinessSubscriptionStatusSchema };

// ========================================
// SUBSCRIPTION PLAN LIMITS
// ========================================

export const PlanLimitsSchema = z.object({
  apiCalls: z.number().int(), // -1 for unlimited
  users: z.number().int(), // -1 for unlimited
  storage: z.number().int(), // In GB, -1 for unlimited
});

export type PlanLimits = z.infer<typeof PlanLimitsSchema>;

// ========================================
// SUBSCRIPTION PLAN DTOs
// ========================================

export const SubscriptionPlanSchema = z.object({
  id: z.string().uuid(),
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  priceMonthly: z.number().int().nonnegative(), // Amount in cents
  priceYearly: z.number().int().nonnegative().nullable().optional(), // Amount in cents
  currency: z.string().length(3).default('usd'),
  isActive: z.boolean().default(true),
  isPopular: z.boolean().default(false),
  trialDays: z.number().int().nonnegative().default(14),
  stripePriceId: z.string().nullable().optional(),
  features: z.array(z.string()),
  limits: PlanLimitsSchema,
  subscriberCount: z.number().int().nonnegative().optional(), // Added in list response
  createdAt: z.union([z.date(), z.string()]),
  updatedAt: z.union([z.date(), z.string()]),
});

export type SubscriptionPlan = z.infer<typeof SubscriptionPlanSchema>;

export const CreatePlanSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(1000).optional(),
  priceMonthly: z.number().int().positive(),
  priceYearly: z.number().int().positive().optional(),
  currency: z.string().length(3).default('usd'),
  isPopular: z.boolean().default(false),
  trialDays: z.number().int().nonnegative().default(14),
  features: z.array(z.string()),
  limits: PlanLimitsSchema,
});

export type CreatePlan = z.infer<typeof CreatePlanSchema>;

export const UpdatePlanSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(1000).nullable().optional(),
  priceMonthly: z.number().int().positive().optional(),
  priceYearly: z.number().int().positive().nullable().optional(),
  isActive: z.boolean().optional(),
  isPopular: z.boolean().optional(),
  trialDays: z.number().int().nonnegative().optional(),
  features: z.array(z.string()).optional(),
  limits: PlanLimitsSchema.optional(),
});

export type UpdatePlan = z.infer<typeof UpdatePlanSchema>;

// ========================================
// CUSTOMER SUBSCRIPTION DTOs
// ========================================

export const BusinessSubscriptionSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  businessName: z.string().optional(), // Populated in responses
  planId: z.string().uuid(),
  planName: z.string().optional(), // Populated in responses
  status: BusinessSubscriptionStatusSchema,
  currentPeriodStart: z.union([z.date(), z.string()]),
  currentPeriodEnd: z.union([z.date(), z.string()]),
  trialStart: z.union([z.date(), z.string()]).nullable().optional(),
  trialEnd: z.union([z.date(), z.string()]).nullable().optional(),
  cancelledAt: z.union([z.date(), z.string()]).nullable().optional(),
  cancelAtPeriodEnd: z.boolean().default(false),
  stripeSubscriptionId: z.string().nullable().optional(),
  monthlyAmount: z.number().int().nonnegative().optional(), // Populated from plan
  billingCycle: BillingCycleSchema.optional(),
  nextBillingDate: z.union([z.date(), z.string()]).optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.union([z.date(), z.string()]),
  updatedAt: z.union([z.date(), z.string()]).optional(),
});

export type BusinessSubscription = z.infer<typeof BusinessSubscriptionSchema>;

export const CreateBusinessSubscriptionSchema = z.object({
  businessId: z.string().uuid(),
  planId: z.string().uuid(),
  billingCycle: BillingCycleSchema.default(BillingCycle.MONTHLY),
  startTrial: z.boolean().default(true),
});

export type CreateBusinessSubscription = z.infer<typeof CreateBusinessSubscriptionSchema>;

export const UpdateBusinessSubscriptionSchema = z.object({
  planId: z.string().uuid().optional(),
  cancelAtPeriodEnd: z.boolean().optional(),
});

export type UpdateBusinessSubscription = z.infer<typeof UpdateBusinessSubscriptionSchema>;

// ========================================
// QUERY SCHEMAS
// ========================================

export const SubscriptionListQuerySchema = z.object({
  status: BusinessSubscriptionStatusSchema.optional(),
  planId: z.string().uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  offset: z.coerce.number().int().min(0).optional().default(0),
});

export type SubscriptionListQuery = z.infer<typeof SubscriptionListQuerySchema>;

export const PlanListQuerySchema = z.object({
  includeInactive: z.coerce.boolean().optional().default(false),
});

export type PlanListQuery = z.infer<typeof PlanListQuerySchema>;

// ========================================
// RESPONSE SCHEMAS
// ========================================

export const SubscriptionListResponseSchema = z.object({
  subscriptions: z.array(BusinessSubscriptionSchema),
  total: z.number().int().nonnegative(),
});

export type SubscriptionListResponse = z.infer<typeof SubscriptionListResponseSchema>;

export const PlanListResponseSchema = z.object({
  plans: z.array(SubscriptionPlanSchema),
  total: z.number().int().nonnegative(),
});

export type PlanListResponse = z.infer<typeof PlanListResponseSchema>;
